import type { NavItem } from '@/shared/constants/navigation';
import { cn } from '@/shared/lib/utils';

/**
 * O que aparece à esquerda do rótulo de um item de navegação: o glifo do Lucide
 * dos itens estáticos, ou o **monograma** dos itens que vêm de vínculo.
 *
 * Existe como componente porque as DUAS navegações o desenham — a coluna e a
 * fileira do celular —, e um monograma que aparecesse só numa delas seria pior
 * que nenhum: a barra recolhida no desktop e a topbar em 375px são exatamente as
 * duas telas onde o rótulo não cabe e o glifo é a única identificação.
 *
 * ⚠️ **O ramo do `typeof` não é defensivo, é obrigatório.** `NavItem.icon` é
 * `LucideIcon | string`, e o `JSX.ElementType` do React 18 aceita `string` — um
 * `<item.icon />` cru compila sem erro e vira `<ML>` no DOM, que o React monta
 * como elemento desconhecido. O compilador não pega este; o ramo pega.
 */
export function NavGlyph({ icon, className }: { icon: NavItem['icon']; className?: string }) {
  if (typeof icon !== 'string') {
    const Icon = icon;
    return <Icon className={cn('size-[18px] shrink-0', className)} aria-hidden="true" />;
  }

  return (
    <span
      aria-hidden="true"
      className={cn(
        // A mesma caixa de 18px do glifo, para que os itens das duas seções
        // fiquem no mesmo eixo vertical na coluna recolhida de 68px.
        'flex size-[18px] shrink-0 items-center justify-center rounded-[5px]',
        // `border-current`: a moldura acompanha a cor do item, inclusive a troca
        // para `text-primary` quando ele é o ativo. Contorno em vez de preenchimento
        // porque o fundo do item ativo já é a marca deslizante — dois preenchimentos
        // empilhados viram um borrão.
        'border border-current text-[8px] font-semibold leading-none tracking-tight',
        className,
      )}
    >
      {icon}
    </span>
  );
}
